import React, { useEffect, useMemo, useRef, useState } from "react";
import { SlDrawer } from "@shoelace-style/shoelace/dist/react";
import { CSVLink } from "react-csv";
import API from "../../../services/api";
import { formatAmount } from "../../../utils/constants";

const BrandDetails = ({ open, onClose, title, url, payload }) => {
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const csvLinkRef = useRef(null);

  useEffect(() => {
    const fetchDetails = async () => {
      if (!open || !url) return;
      setIsLoading(true);
      try {
        const result = await API({
          url,
          method: "POST",
          data: payload,
        });

        if (result.status === "SUCCESS") {
          setRows(result.data || []);
        } else {
          setRows([]);
        }
      } catch (error) {
        setRows([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDetails();
  }, [open, url, payload]);

  const parseRevenue = (value) => {
    if (value === null || value === undefined) return null;
    if (typeof value === "number") return Number.isFinite(value) ? value : null;
    if (typeof value === "string") {
      const cleaned = value.replace(/,/g, "").trim();
      if (cleaned === "") return null;
      const parsed = Number(cleaned);
      return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
  };

  const getRevenueDisplay = (value) => {
    const parsed = parseRevenue(value);
    return parsed === null ? "-" : formatAmount(parsed);
  };

  const getRevenueTitle = (value) => {
    const parsed = parseRevenue(value);
    return parsed === null ? "-" : parsed;
  };

  const totalW2n = rows.reduce((sum, row) => sum + (parseRevenue(row.w2n_revenue) ?? 0), 0);
  const totalEenadu = rows.reduce((sum, row) => sum + (parseRevenue(row.eenadu_revenue) ?? 0), 0);

  const csvData = useMemo(() => {
    if (!rows.length) return [];
    const baseRows = rows.map((row) => ({
      "Brand Name": row.brand_name ?? "",
      "Sales Person Name": row.sales_person_name ?? "",
      "Brand Category": row.brand_category ?? "",
      "Brand Location": row.brand_location ?? "",
      "W2N Revenue": parseRevenue(row.w2n_revenue) ?? "",
      "Eenadu Revenue": parseRevenue(row.eenadu_revenue) ?? "",
    }));

    baseRows.push({
      "Brand Name": "Total",
      "Sales Person Name": "",
      "Brand Category": "",
      "Brand Location": "",
      "W2N Revenue": totalW2n,
      "Eenadu Revenue": totalEenadu,
    });

    return baseRows;
  }, [rows, totalW2n, totalEenadu]);

  if (!open) return null;

  return (
    <SlDrawer open={open} onSlAfterHide={onClose} onSlRequestClose={onClose} style={{ "--size": "60%" }}>
      <div className="p-4">
        <div className="flex items-center justify-between gap-4 mb-3">
          <div className="text-lg font-semibold">{title || "Brand Details"}</div>
          <div>
            <CSVLink
              data={csvData}
              filename="brand_details.csv"
              className="hidden"
              ref={csvLinkRef}
            />
            <button
              type="button"
              className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-60"
              onClick={() => csvLinkRef.current?.link?.click()}
              disabled={!csvData.length}
            >
              Download
            </button>
          </div>
        </div>

        {isLoading ? (
          <div className="text-sm text-gray-600">Loading...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="table-auto w-full border border-gray-300 text-sm">
              <thead className="bg-gray-100">
                <tr>
                  <th className="border px-2 py-1">Brand Name</th>
                  <th className="border px-2 py-1">Sales Person Name</th>
                  <th className="border px-2 py-1">Brand Category</th>
                  <th className="border px-2 py-1">Brand Location</th>
                  <th className="border px-2 py-1">W2N Revenue</th>
                  <th className="border px-2 py-1">Eenadu Revenue</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td className="border px-2 py-2 text-center" colSpan={8}>
                      No records found
                    </td>
                  </tr>
                ) : (
                  rows.map((row, idx) => (
                    <tr key={`${row.brand_id || "brand"}-${idx}`}>
                      <td className="border px-2 py-1">{row.brand_name ?? "-"}</td>
                      <td className="border px-2 py-1">{row.sales_person_name ?? "-"}</td>
                      <td className="border px-2 py-1">{row.brand_category ?? "-"}</td>
                      <td className="border px-2 py-1">{row.brand_location ?? "-"}</td>
                      <td className="border px-2 py-1" title={getRevenueTitle(row.w2n_revenue)}>
                        {getRevenueDisplay(row.w2n_revenue)}
                      </td>
                      <td className="border px-2 py-1" title={getRevenueTitle(row.eenadu_revenue)}>
                        {getRevenueDisplay(row.eenadu_revenue)}
                      </td>
                    </tr>
                  ))
                )}
                {rows.length > 0 && (
                  <tr className="bg-gray-50 font-semibold">
                    <td className="border px-2 py-1">Total</td>
                    <td className="border px-2 py-1" />
                    <td className="border px-2 py-1" />
                    <td className="border px-2 py-1" />
                    <td className="border px-2 py-1" title={totalW2n}>
                      {formatAmount(totalW2n)}
                    </td>
                    <td className="border px-2 py-1" title={totalEenadu}>
                      {formatAmount(totalEenadu)}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </SlDrawer>
  );
};

export default BrandDetails;
